import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useWfWorkplaceService from 'src/api/wf/service/WfWorkplaceService';
import {
  useWfFilterStore,
  WfFilterParameters,
  WfReservedOptions,
} from 'src/api/wf/stores/WfFilterStore';
import { useWfUserStore } from 'src/api/wf/stores/WfUserStore';
import {
  WfOperationStatus,
  operationStatusCodes,
  sortStatusOptions,
  defaultSatusOrder,
} from 'src/api/wf/models/enums/WfOperationtStatus';
import { OptionProps } from '../../select/DropdownSelect';

type SearchErrors = {
  branch?: string;
  officeOrCustomer?: string;
};

export const useMapSearch = (onSearch: () => void) => {
  const { t } = useTranslation();

  const { user } = useWfUserStore();
  const {
    filterParameters,
    patchFilterParameters,
    filtersInitialized,
    setFiltersInitialized,
    setSalesOfficeOptions,
    setSectionOptions,
    setSquadOptions,
    branchOptions,
    salesOfficeOptions,
    sectionOptions,
    squadOptions,
    statusOptions,
  } = useWfFilterStore();
  const {
    fetchBranchOptions,
    fetchSalesOfficeOptions,
    fetchSectionOptions,
    fetchSquadOptions,
    fetchStatusOptions,
    fetchUserSalesOffice,
  } = useWfWorkplaceService();

  const [localFilterParameters, setLocalFilterParameters] = useState<WfFilterParameters>(() => ({
    ...filterParameters,
  }));
  const [errors, setErrors] = useState<SearchErrors>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [searchPending, setSearchPending] = useState(false);

  const handleOptionsFetchError = useCallback((error: unknown) => {
    // 支社等セレクターは既存の選択肢を保持するため、ここでは結果表示は行わずログのみ
    console.error(error);
  }, []);

  useEffect(() => {
    if (!user || filtersInitialized) return;

    const initialParams = {
      branch: user.branchCode,
      salesOffice: user.salesOfficeCode,
      section: user.sectionCode,
      squad: user.squadCode,
    };
    patchFilterParameters(initialParams);
    setLocalFilterParameters((prev) => ({
      ...prev,
      branch: user.branchCode ?? '',
      salesOffice: user.salesOfficeCode ?? '',
      section: user.sectionCode ?? '',
      squad: user.squadCode ?? '',
    }));
    setFiltersInitialized(true);
  }, [user, filtersInitialized, patchFilterParameters, setFiltersInitialized]);

  useEffect(() => {
    if (!user?.branchCode || !user.salesOfficeCode) return;
    fetchUserSalesOffice(user.branchCode, user.salesOfficeCode);
  }, [fetchUserSalesOffice, user]);

  useEffect(() => {
    fetchBranchOptions().catch(handleOptionsFetchError);
    fetchStatusOptions().catch(handleOptionsFetchError);
  }, [fetchBranchOptions, fetchStatusOptions, handleOptionsFetchError]);

  useEffect(() => {
    const { branch, salesOffice, section } = localFilterParameters;

    if (branch) {
      fetchSalesOfficeOptions(branch).catch(handleOptionsFetchError);
    } else {
      setSalesOfficeOptions([]);
    }

    if (branch && salesOffice) {
      fetchSectionOptions(branch, salesOffice).catch(handleOptionsFetchError);
    } else {
      setSectionOptions([]);
    }

    if (branch && salesOffice && section) {
      fetchSquadOptions(branch, salesOffice, section).catch(handleOptionsFetchError);
    } else {
      setSquadOptions([]);
    }
  }, [
    localFilterParameters,
    fetchSalesOfficeOptions,
    fetchSectionOptions,
    fetchSquadOptions,
    setSalesOfficeOptions,
    setSectionOptions,
    setSquadOptions,
    handleOptionsFetchError,
  ]);

  const dropdownOptions = useMemo(() => {
    const createOptions = (items: { code: string; name: string }[]): OptionProps[] =>
      items.map((item) => ({ title: `${item.code} ${item.name}`, value: item.code }));

    const getStatusTranslation = (key: string): string => {
      const translationKey = `wf.statusCodes.${key}`;
      const translatedValue = t(translationKey);
      return translatedValue === translationKey ? key : translatedValue;
    };

    const customStatusOptions = [
      {
        groupName: WfOperationStatus.Incomplete,
        statuses: operationStatusCodes(WfOperationStatus.Incomplete),
      },
      { groupName: WfOperationStatus.None, statuses: operationStatusCodes(WfOperationStatus.None) },
    ];
    const fullStatusOptions = [...statusOptions, ...customStatusOptions];

    return {
      branch: createOptions(branchOptions),
      salesOffice: createOptions(salesOfficeOptions),
      section: createOptions(sectionOptions),
      squad: squadOptions.map((s) => ({ title: s.code, value: s.code })),
      reserved: [
        { title: t('wf.reservationStatus.all'), value: WfReservedOptions.all },
        { title: t('wf.reservationStatus.reserved'), value: WfReservedOptions.reserved },
        { title: t('wf.reservationStatus.unreserved'), value: WfReservedOptions.unreserved },
      ],
      status: sortStatusOptions(fullStatusOptions, 'groupName', defaultSatusOrder).map((group) => ({
        title: getStatusTranslation(group.groupName),
        value: group.groupName,
      })),
      _fullStatusOptions: fullStatusOptions,
    };
  }, [branchOptions, salesOfficeOptions, sectionOptions, squadOptions, statusOptions, t]);

  const handleFieldChange = useCallback(
    <K extends keyof WfFilterParameters>(field: K, value: WfFilterParameters[K]) => {
      setErrors({});

      const updatedParams: Partial<WfFilterParameters> = { [field]: value };

      if (field === 'branch') {
        updatedParams.salesOffice = '';
        updatedParams.section = '';
        updatedParams.squad = '';
      } else if (field === 'salesOffice') {
        updatedParams.section = '';
        updatedParams.squad = '';
      } else if (field === 'section') {
        updatedParams.squad = '';
      }

      if (field === 'statusList') {
        const selectedGroup = dropdownOptions._fullStatusOptions.find(
          (group) => group.groupName === value
        );
        updatedParams.statusList =
          (selectedGroup?.groupName as WfOperationStatus) || WfOperationStatus.None;
      }

      setLocalFilterParameters((prev) => ({ ...prev, ...updatedParams }));
    },
    [dropdownOptions._fullStatusOptions]
  );

  useEffect(() => {
    if (!searchPending) return;
    setSearchPending(false);
    onSearch();
    setHasSearched(true);
  }, [searchPending, onSearch]);

  const handleSearch = useCallback(() => {
    const validationErrors: SearchErrors = {};
    if (!localFilterParameters.branch) {
      validationErrors.branch = t('wf.sites.missingBranchOffice');
    }
    if (!localFilterParameters.salesOffice && !localFilterParameters.customerNumber) {
      validationErrors.officeOrCustomer = t('wf.searchHeader.noBranchOrCustomerNumber');
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    patchFilterParameters({ ...localFilterParameters });
    setSearchPending(true);
  }, [localFilterParameters, patchFilterParameters, t]);

  return {
    filterParameters: localFilterParameters,
    errors,
    hasSearched,
    dropdownOptions,
    handleFieldChange,
    handleSearch,
  };
};
